<template>
  <div class="ai-chat-bot">
    <button class="ai-chat-bot-btn" @click="openChatbot">LMD AI</button>
    <div class="ai-chat-bot-in">
      <div class="ai-chat-bot-top">
        <div class="ai-chat-bot-top-left">
          <img src="@/assets/images/logo.svg" alt="AI Chat Bot" />
          <h3>LMD AI</h3>
        </div>
        <div class="ai-chat-bot-top-right">
          <button class="chat-bat-minimize-btn"></button>
          <button class="chat-bat-zoom-btn"></button>
        </div>
      </div>
      <div class="ai-chat-bot-btm">
        <div
          class="ai-chat-bot-message-list-box"
          ref="scrollWrapper"
          data-simplebar
        >
          <div class="ai-chat-bot-message-list-box-in">
            <div
              v-for="message in messages"
              :key="message.id"
              class="bot-message-box"
              :class="
                message.is_user
                  ? 'ai-bot user_message_box'
                  : 'ai_bot_message_box'
              "
            >
              <div class="bot-message-box-in">
                <div
                  class="ai-chat-bot-message-show"
                  v-html="formatMessage(message.message)"
                ></div>
                <div class="bot-message-box-action">
                  <ul>
                    <li>{{ formatTime(message.created_at) }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isLoading" class="bot-is-typing">
            Generating<span>...</span>
          </div>
        </div>
        <div class="ai-chat-bot-message-write-box">
          <div class="ai-chat-bot-message-write-box-top">
            <textarea
              v-model="userInput"
              class="ai-chat-bot-message-write-box-field"
              placeholder="Type your message here..."
              @keyup.enter="sendMessage()"
              :disabled="isLoading"
            ></textarea>
            <button class="send-message-btn" @click="sendMessage()"></button>
          </div>
          <div class="ai-chat-bot-message-write-box-btm">
            <ul>
              <li><button class="ai-chat-bot-log-btn"></button></li>
              <li>
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileUpload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style="display: none"
                />
                <button
                  class="ai-chat-bot-attach-btn"
                  @click="triggerFileUpload"
                  v-if="isFileUploadEnabled"
                ></button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, computed } from "vue";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { API_BASE_URL } from "@/config/api.config";
import moment from "moment";
import SimpleBar from "simplebar";

interface Message {
  id: number;
  is_user: boolean;
  message: string;
  created_at?: string;
  updated_at?: string;
}

export default defineComponent({
  name: "ChatbotPopup",
  data() {
    return {
      messages: [] as Message[],
      userInput: "",
      sessionId: null as string | null,
      isLoading: false,
      simpleBarInstance: null as any,
      selectedFile: null as File | null,
    };
  },
  mounted() {
    const el = this.$refs.scrollWrapper as HTMLElement;
    if (el) {
      this.simpleBarInstance = new SimpleBar(el);
    }
  },
  computed: {
    isFileUploadEnabled(): boolean {
      const lastMessage = this.messages[this.messages.length - 1];
      if (lastMessage && !lastMessage.is_user) {
        const messageText = lastMessage.message.toLowerCase();
        return (
          messageText.includes("resume") || messageText.includes("picture") || messageText.includes("image") || messageText.includes("photo") || messageText.includes("profile")
        );
      }
      return false;
    },
  },
  methods: {
    async openChatbot() {
      this.initializeSession();
      await this.fetchHistory();
      if (this.messages.length === 0) {
        await this.sendMessage("Initialize chatbot");
      }
    },
    initializeSession() {
      let sessionId = sessionStorage.getItem("chat_session_id");
      if (!sessionId) {
        sessionId = uuidv4();
        sessionStorage.setItem("chat_session_id", sessionId);
      }
      this.sessionId = sessionId;
    },
    async fetchHistory() {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/chat-history/?session_id=${this.sessionId}`
        );
        this.messages = response.data;
        this.scrollToBottom();
      } catch (error) {
        console.error("Error fetching history:", error);
      }
    },
    async sendMessage(customMessage?: string) {
      // Handle Initialize chatbot case
      if (customMessage && customMessage == "Initialize chatbot") {
        await axios.post(`${API_BASE_URL}/chat-message/`, {
          session_id: this.sessionId,
          message: customMessage,
        });
        this.fetchHistory();
        return;
      }

      // Handle clickable messages and user input
      let messageToSend = customMessage || this.userInput;
      if (!messageToSend) return;

      // Clear user input if it was typed
      if (!customMessage) {
        this.userInput = "";
      }

      // Ensure session is initialized
      if (!this.sessionId) {
        this.initializeSession();
      }

      // Add user message to the list
      this.messages.push({
        id: Date.now(),
        is_user: true,
        message: messageToSend,
      });

      this.scrollToBottom();
      this.isLoading = true;

      try {
        const formData = new FormData();
        formData.append("session_id", this.sessionId!);
        formData.append("message", messageToSend);

        if (this.selectedFile) {
          formData.append("file", this.selectedFile);
          console.log("File being sent:", this.selectedFile);
        }

        await axios.post(`${API_BASE_URL}/chat-message/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.fetchHistory();
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        this.isLoading = false;
        this.selectedFile = null;
        this.selectedFile = null;
      }
    },
    formatTime(dateString: string | undefined): string {
      if (!dateString) return "";

      return moment(dateString).format("h:mm A");
    },
    scrollToBottom() {
      nextTick(() => {
        if (this.simpleBarInstance) {
          const scrollElement = this.simpleBarInstance.getScrollElement();
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      });
    },
    formatMessage(message: string): string {
      return message.replace(/\n/g, "<br>");
    },
    triggerFileUpload() {
      (this.$refs.fileInput as HTMLInputElement).click();
    },
    async handleFileUpload(event: Event) {
      const target = event.target as HTMLInputElement;
      this.selectedFile = target.files?.[0] || null;

      if (!this.selectedFile) return;

      const lastMessage = this.messages[this.messages.length - 1];
      if (lastMessage && !lastMessage.is_user) {
        const messageText = lastMessage.message.toLowerCase();

        if (messageText.includes("resume")) {
          await this.sendMessage("resume uploaded");
        } else if (messageText.includes("picture") || messageText.includes("image") || messageText.includes("photo") || messageText.includes("profile")) {
          await this.sendMessage("picture uploaded");
        } else {
          this.selectedFile = null;
          target.value = "";
          return;
        }
      } else {
        this.selectedFile = null;
        target.value = "";
        return;
      }
      // Clear the input
      target.value = "";
    },
  },
});
</script>

<style scoped>
.clickable-message {
  background-color: #e3f2fd !important;
  border: 1px solid #2196f3 !important;
  color: #1976d2 !important;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  margin: 2px 0 !important;
  display: inline-block !important;
}

.clickable-message:hover {
  background-color: #bbdefb !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}
</style>
